import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute } from "wouter";
import { Loader2, Plus, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DetailPageShell } from "@/components/layout/detail-page-shell";
import { InlineTextField } from "@/components/inline-edit/inline-text-field";
import { InlineSelectField } from "@/components/inline-edit/inline-select-field";
import { InlineTextareaField } from "@/components/inline-edit/inline-textarea-field";
import { InlineSwitchField } from "@/components/inline-edit/inline-switch-field";
import { useToast } from "@/hooks/use-toast";
import { useTabState } from "@/hooks/use-tab-state";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { ProductFormModal } from "@/components/lenders/product-form-modal";
import { FeeGroupFormModal } from "@/components/lenders/fee-group-form-modal";
import { PricingTierFormModal } from "@/components/lenders/pricing-tier-form-modal";
import { FeeFormModal } from "@/components/lenders/fee-form-modal";
import { CommissionFormModal } from "@/components/lenders/commission-form-modal";
import { LenderFormModal } from "@/components/lenders/lender-form-modal";
import { FeesPanel } from "@/components/lenders/fees-panel";
import { ProductsPanel } from "@/components/lenders/products-panel";
import { DocumentsPanel } from "@/components/lenders/documents-panel";
import { LenderIncomeShadingPanel } from "@/components/financial/panels/lender-income-shading-panel";

// Lender type from schema - aligned with database enums
type LenderType = "bank" | "non_bank" | "private" | "smsf_lender";
type LenderStatus = "active" | "inactive" | "suspended";
type AccreditationStatus = "active" | "pending" | "expired" | "renewal_due";

interface Lender {
  id: string;
  lenderCode: string;
  lenderName: string;
  lenderType: LenderType;
  status: LenderStatus;
  accreditationStatus: AccreditationStatus | null;
  accreditationDate: string | null;
  accreditationExpiry: string | null;
  upfrontCommissionRate: string | null;
  trailCommissionRate: string | null;
  clawbackPeriodMonths: number | null;
  bdmName: string | null;
  bdmEmail: string | null;
  bdmPhone: string | null;
  bdmMobile: string | null;
  notes: string | null;
  applicationPortalUrl: string | null;
  apiIntegrationEnabled: boolean | null;
  createdAt: string;
  updatedAt: string;
}

interface LenderProduct {
  id: string;
  lenderId: string;
  productCode: string;
  productName: string;
  productCategory: string;
  loanType: string | null;
  interestRateType: string | null;
  baseRate: string | null;
  minLoanAmount: string | null;
  maxLoanAmount: string | null;
  maxLvr: string | null;
  maxLoanTerm: number | null;
  upfrontCommissionRate: string | null;
  trailCommissionRate: string | null;
  isActive: boolean;
  productDescription: string | null;
}

interface LenderFeeGroup {
  id: string;
  lenderId: string;
  groupName: string;
  description: string | null;
  isDefault: boolean;
}

const LENDER_TYPE_OPTIONS = [
  { value: "bank", label: "Bank" },
  { value: "non_bank", label: "Non-Bank" },
  { value: "private", label: "Private" },
  { value: "smsf_lender", label: "SMSF Lender" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "suspended", label: "Suspended" },
];

const ACCREDITATION_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "expired", label: "Expired" },
  { value: "renewal_due", label: "Renewal Due" },
];

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function LenderDetail() {
  const [, params] = useRoute("/lenders/:id");
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useTabState("overview");

  // Modal states
  const [editLenderModalOpen, setEditLenderModalOpen] = useState(false);
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);
  const [addFeeGroupModalOpen, setAddFeeGroupModalOpen] = useState(false);
  const [addPricingTierModalOpen, setAddPricingTierModalOpen] = useState(false);
  const [addFeeModalOpen, setAddFeeModalOpen] = useState(false);
  const [addCommissionModalOpen, setAddCommissionModalOpen] = useState(false);

  // Parent context for child modals
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [selectedFeeGroupId, setSelectedFeeGroupId] = useState<string>("");

  const lenderId = params?.id;

  // Fetch lender data
  const { data: lender, isLoading } = useQuery<Lender>({
    queryKey: [`/api/crm/lenders/${lenderId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/crm/lenders/${lenderId}`);
      return response.json();
    },
    enabled: !!lenderId,
  });

  // Fetch products
  const { data: productsData } = useQuery<LenderProduct[]>({
    queryKey: [`/api/crm/lenders/${lenderId}/products`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/crm/lenders/${lenderId}/products`);
      return response.json();
    },
    enabled: !!lenderId,
  });

  const products = Array.isArray(productsData) ? productsData : [];

  // Fetch fee groups
  const { data: feeGroupsData } = useQuery<LenderFeeGroup[]>({
    queryKey: [`/api/crm/lenders/${lenderId}/fee-groups`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/crm/lenders/${lenderId}/fee-groups`);
      return response.json();
    },
    enabled: !!lenderId,
  });

  const feeGroups = Array.isArray(feeGroupsData) ? feeGroupsData : [];

  // Fetch document checklists count
  const { data: documentChecklistsData } = useQuery<{ id: string }[]>({
    queryKey: ["/api/crm/lenders", lenderId, "document-checklists"],
    enabled: !!lenderId,
  });

  const documentChecklists = Array.isArray(documentChecklistsData) ? documentChecklistsData : [];

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Lender>) => {
      const response = await apiRequest("PATCH", `/api/crm/lenders/${lenderId}`, updates);
      if (!response.ok) {
        throw new Error("Failed to update lender");
      }
      return response.json();
    },
    onSuccess: () => {
      // Invalidate both the list and detail queries to ensure cache consistency
      // Pattern matches account-detail.tsx and contact-detail.tsx
      queryClient.invalidateQueries({ queryKey: ["/api/crm/lenders"] });
      queryClient.invalidateQueries({ queryKey: [`/api/crm/lenders/${lenderId}`] });
      toast({ title: "Success", description: "Lender updated successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleUpdate = async (field: string, value: any) => {
    await updateMutation.mutateAsync({ [field]: value });
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!lender) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <p className="text-muted-foreground">Lender not found</p>
      </div>
    );
  }

  const getStatusBadgeColor = (status: LenderStatus) => {
    switch (status) {
      case "active":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200";
      case "inactive":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
      case "suspended":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <>
      <DetailPageShell
        title={lender.lenderName}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Lenders", href: "/lenders" },
          { label: lender.lenderName },
        ]}
        value={activeTab}
        onValueChange={setActiveTab}
        badge={
          <Badge className={getStatusBadgeColor(lender.status)}>
            {STATUS_OPTIONS.find((s) => s.value === lender.status)?.label || lender.status}
          </Badge>
        }
        navItems={[
          { label: "Overview", href: "#overview" },
          { label: "Products", href: "#products", count: products.length },
          { label: "Fees", href: "#fees", count: feeGroups.length },
          { label: "Commissions", href: "#commissions" },
          { label: "Documents", href: "#documents", count: documentChecklists.length },
        ]}
        tabs={[
          {
            value: "overview",
            label: "Overview",
            content: (
              <div className="grid gap-4 md:grid-cols-2">
                {/* Basic Information */}
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="mb-4 font-semibold">Basic Information</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineTextField
                        label="Lender Code"
                        value={lender.lenderCode}
                        onSave={(value) => handleUpdate("lenderCode", value)}
                        disabled={updateMutation.isPending}
                      />
                      <InlineTextField
                        label="Lender Name"
                        value={lender.lenderName}
                        onSave={(value) => handleUpdate("lenderName", value)}
                        disabled={updateMutation.isPending}
                      />
                      <InlineSelectField
                        label="Lender Type"
                        value={lender.lenderType}
                        options={LENDER_TYPE_OPTIONS}
                        onSave={(value) => handleUpdate("lenderType", value)}
                        disabled={updateMutation.isPending}
                      />
                      <InlineSelectField
                        label="Status"
                        value={lender.status}
                        options={STATUS_OPTIONS}
                        onSave={(value) => handleUpdate("status", value)}
                        disabled={updateMutation.isPending}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* BDM Contact */}
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="mb-4 font-semibold">BDM Contact</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineTextField
                        label="BDM Name"
                        value={lender.bdmName || ""}
                        onSave={(value) => handleUpdate("bdmName", value)}
                        disabled={updateMutation.isPending}
                      />
                      <InlineTextField
                        label="BDM Email"
                        value={lender.bdmEmail || ""}
                        onSave={(value) => handleUpdate("bdmEmail", value)}
                        disabled={updateMutation.isPending}
                      />
                      <InlineTextField
                        label="BDM Phone"
                        value={lender.bdmPhone || ""}
                        onSave={(value) => handleUpdate("bdmPhone", value)}
                        disabled={updateMutation.isPending}
                      />
                      <InlineTextField
                        label="BDM Mobile"
                        value={lender.bdmMobile || ""}
                        onSave={(value) => handleUpdate("bdmMobile", value)}
                        disabled={updateMutation.isPending}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Accreditation */}
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="mb-4 font-semibold">Accreditation</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineSelectField
                        label="Accreditation Status"
                        value={lender.accreditationStatus || "not_accredited"}
                        options={ACCREDITATION_STATUS_OPTIONS}
                        onSave={(value) => handleUpdate("accreditationStatus", value)}
                        disabled={updateMutation.isPending}
                      />
                      <div className="flex flex-col space-y-1">
                        <span className="text-sm text-muted-foreground">Accreditation Date</span>
                        <span className="text-sm">{formatDate(lender.accreditationDate)}</span>
                      </div>
                      <div className="flex flex-col space-y-1">
                        <span className="text-sm text-muted-foreground">Expiry Date</span>
                        <span className="text-sm">{formatDate(lender.accreditationExpiry)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Default Commission Rates */}
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="mb-4 font-semibold">Default Commission Rates</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineTextField
                        label="Upfront Commission Rate (%)"
                        value={lender.upfrontCommissionRate ? (parseFloat(lender.upfrontCommissionRate) * 100).toFixed(2) : ""}
                        onSave={async (value) => {
                          const decimal = parseFloat(value) / 100;
                          await handleUpdate("defaultUpfrontRate", decimal.toString());
                        }}
                        disabled={updateMutation.isPending}
                      />
                      <InlineTextField
                        label="Trail Commission Rate (%)"
                        value={lender.trailCommissionRate ? (parseFloat(lender.trailCommissionRate) * 100).toFixed(2) : ""}
                        onSave={async (value) => {
                          const decimal = parseFloat(value) / 100;
                          await handleUpdate("defaultTrailRate", decimal.toString());
                        }}
                        disabled={updateMutation.isPending}
                      />
                      <InlineTextField
                        label="Clawback Period (months)"
                        value={lender.clawbackPeriodMonths?.toString() || ""}
                        onSave={async (value) => {
                          const months = parseInt(value);
                          await handleUpdate("clawbackPeriodMonths", months);
                        }}
                        disabled={updateMutation.isPending}
                      />
                      <div className="col-span-2 rounded bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        These are default rates. Product-specific rates can override these in the Products tab.
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Integration Settings */}
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="mb-4 font-semibold">Integration Settings</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineTextField
                        label="Application Portal URL"
                        value={lender.applicationPortalUrl || ""}
                        onSave={(value) => handleUpdate("applicationPortalUrl", value)}
                        disabled={updateMutation.isPending}
                      />
                      <InlineSwitchField
                        label="API Integration Enabled"
                        value={lender.apiIntegrationEnabled || false}
                        onSave={(value) => handleUpdate("apiIntegrationEnabled", value)}
                        disabled={updateMutation.isPending}
                        testId="switch-api-integration-inline"
                      />
                      <div className="col-span-2 rounded bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        API integration allows automated application submission and status updates.
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Notes */}
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="mb-4 font-semibold">Notes</h3>
                    <div className="space-y-2">
                      <InlineTextareaField
                        label="Internal Notes"
                        value={lender.notes || ""}
                        onSave={(value) => handleUpdate("notes", value)}
                        disabled={updateMutation.isPending}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "products",
            label: `Products (${products.length})`,
            content: lenderId ? (
              <div className="space-y-6">
                <ProductsPanel lenderId={lenderId} />
                <LenderIncomeShadingPanel lenderId={lenderId} />
              </div>
            ) : null,
          },
          {
            value: "fees",
            label: `Fees (${feeGroups.length})`,
            content: lenderId ? <FeesPanel lenderId={lenderId} /> : null,
          },
          {
            value: "commissions",
            label: "Commissions",
            content: (
              <Card>
                <CardContent className="pt-6">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="font-semibold">Commission Structures</h3>
                  </div>
                  <p className="text-center text-muted-foreground">
                    Commission structures are product-specific. View commissions in the Products tab.
                  </p>
                </CardContent>
              </Card>
            ),
          },
          {
            value: "documents",
            label: "Documents",
            content: (
              <Card>
                <CardContent className="pt-6">
                  <DocumentsPanel lenderId={lenderId!} />
                </CardContent>
              </Card>
            ),
          },
        ]}
        rightPanel={
          <div className="space-y-4">
            {/* Key Dates */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">KEY DATES</h3>
              <div className="space-y-2 text-xs">
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-medium">
                    {lender.createdAt
                      ? new Date(lender.createdAt).toLocaleDateString("en-AU", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Modified</p>
                  <p className="font-medium">
                    {lender.updatedAt
                      ? new Date(lender.updatedAt).toLocaleDateString("en-AU", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="border-t pt-3">
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">QUICK STATS</h3>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Products:</span>
                  <span className="font-medium">{products.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fee Groups:</span>
                  <span className="font-medium">{feeGroups.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Documents:</span>
                  <span className="font-medium">{documentChecklists.length}</span>
                </div>
              </div>
            </div>

            {/* BDM Contact Quick View */}
            {lender.bdmName && (
              <div className="border-t pt-3">
                <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">BDM CONTACT</h3>
                <div className="space-y-1 text-xs">
                  <p className="font-medium">{lender.bdmName}</p>
                  {lender.bdmEmail && (
                    <a
                      href={`mailto:${lender.bdmEmail}`}
                      className="text-primary hover:underline block"
                    >
                      {lender.bdmEmail}
                    </a>
                  )}
                  {lender.bdmMobile && (
                    <a
                      href={`tel:${lender.bdmMobile}`}
                      className="text-muted-foreground hover:text-foreground block"
                    >
                      {lender.bdmMobile}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="border-t pt-3">
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">ACTIONS</h3>
              <div className="space-y-2">
                <Button
                  size="sm"
                  className="w-full justify-start text-xs"
                  variant="outline"
                  onClick={() => setEditLenderModalOpen(true)}
                  data-testid="button-edit-lender"
                >
                  <Edit className="h-3 w-3 mr-1" />
                  Edit Lender
                </Button>
                <Button
                  size="sm"
                  className="w-full justify-start text-xs"
                  variant="outline"
                  onClick={() => setAddProductModalOpen(true)}
                  data-testid="button-add-product-sidebar"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Product
                </Button>
                <Button
                  size="sm"
                  className="w-full justify-start text-xs"
                  variant="outline"
                  onClick={() => setAddFeeGroupModalOpen(true)}
                  data-testid="button-add-fee-group-sidebar"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Fee Group
                </Button>
              </div>
            </div>
          </div>
        }
      />

      {/* Edit Lender Modal */}
      <LenderFormModal
        open={editLenderModalOpen}
        onOpenChange={setEditLenderModalOpen}
        lender={lender as any}
        onLenderCreated={() => {
          queryClient.invalidateQueries({ queryKey: [`/api/crm/lenders/${lenderId}`] });
          queryClient.invalidateQueries({ queryKey: ["/api/crm/lenders"] });
        }}
      />

      {/* Product Form Modal */}
      <ProductFormModal
        open={addProductModalOpen}
        onOpenChange={setAddProductModalOpen}
        defaultLenderId={lenderId}
        onProductCreated={() => {
          queryClient.invalidateQueries({ queryKey: [`/api/crm/lenders/${lenderId}/products`] });
        }}
      />

      {/* Fee Group Form Modal */}
      {lenderId && (
        <FeeGroupFormModal
          open={addFeeGroupModalOpen}
          onOpenChange={setAddFeeGroupModalOpen}
          lenderId={lenderId}
          onFeeGroupCreated={() => {
            queryClient.invalidateQueries({ queryKey: [`/api/crm/lenders/${lenderId}/fee-groups`] });
          }}
        />
      )}

      {/* Pricing Tier Form Modal */}
      {selectedProductId && (
        <PricingTierFormModal
          open={addPricingTierModalOpen}
          onOpenChange={setAddPricingTierModalOpen}
          lenderProductId={selectedProductId}
          onPricingTierCreated={() => {
            queryClient.invalidateQueries({ queryKey: [`/api/crm/lender-products/${selectedProductId}/pricing-tiers`] });
            setSelectedProductId("");
          }}
        />
      )}

      {/* Fee Form Modal */}
      {selectedFeeGroupId && (
        <FeeFormModal
          open={addFeeModalOpen}
          onOpenChange={setAddFeeModalOpen}
          feeGroupId={selectedFeeGroupId}
          onFeeCreated={() => {
            queryClient.invalidateQueries({ queryKey: [`/api/crm/lender-fee-groups/${selectedFeeGroupId}/fees`] });
            setSelectedFeeGroupId("");
          }}
        />
      )}

      {/* Commission Form Modal */}
      {selectedProductId && (
        <CommissionFormModal
          open={addCommissionModalOpen}
          onOpenChange={setAddCommissionModalOpen}
          lenderProductId={selectedProductId}
          onCommissionCreated={() => {
            queryClient.invalidateQueries({ queryKey: [`/api/crm/lender-products/${selectedProductId}/commissions`] });
            setSelectedProductId("");
          }}
        />
      )}
    </>
  );
}
